import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Task } from '../../types/tasks';
import { deleteTask } from '../../features/tasks/taskSlice';
import EditTaskForm from './EditTaskForm';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { formatDate } from '../../utils/formatters';
import { Clock, CheckCircle, AlertTriangle } from 'react-feather';

interface Props {
  task: Task;
}

const TaskCard: React.FC<Props> = ({ task }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const handleDelete = async () => {
    if (window.confirm(t('common.areYouSure'))) {
      await dispatch(deleteTask(task.id));
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      case 'pending':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  if (isEditing) {
    return (
      <EditTaskForm
        task={task}
        onCancel={() => setIsEditing(false)}
        onSuccess={() => setIsEditing(false)}
      />
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
            {task.description && (
              <p className="text-sm text-gray-600 mt-1">{task.description}</p>
            )}
          </div>
          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
            {getStatusIcon(task.status)}
            {t(`tasks.status.${task.status}`)}
          </span>
        </div>

        {task.due_date && (
          <p className="text-sm text-gray-600">
            {t('tasks.dueDate')}: {formatDate(task.due_date)}
          </p>
        )}

        <div className="flex justify-end space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          >
            {t('common.edit')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default TaskCard;